/** @jsx jsx */
import { jsx, Flex, Box, Progress, Text, Grid } from 'theme-ui'
import Heading from '../shared/Typography/Heading/Heading'
import Body from '../shared/Typography/Body/Body'
import styled from '@emotion/styled'
import Link from '../shared/Typography/Link/Link'
import React, { useState, forwardRef } from 'react'
import Tab from '../shared/Tab/Tab'
import CommentBlock from './CommentBlock'
import { HideOnDestop, HideOnMobile } from '../shared/index'
import Button from '../shared/Buttons/Button'
import QuestionForm from './QuestionForm'
import ReviewForm from './ReviewForm'
import CustomStarRating from '../shared/StarRating/StarRating'
import usePagination from '../../hooks/usePagination'
import Pagination from '../shared/Pagination/Pagination'
import Dropdown from '../shared/Dropdown/Dropdown'

import LightboxImageCarousel from '../shared/LightboxImage'
import TrustPilotLogo from '../../icons/TrustPilotLogo.svg'

const starFilters = [
  { count: 5, text: 'fiveStars' },
  { count: 4, text: 'fourStars' },
  { count: 3, text: 'threeStars' },
  { count: 2, text: 'twoStars' },
  { count: 1, text: 'oneStars' },
]

const renderReviews = (list, options, search, starCountFilter) => {
  let result = list
  if (options) {
    result = list.filter(review => review.category === options.value)

    if (search) {
      let temp = result
      result = temp.filter(review => {
        if (review.commentText) {
          return review.commentText.toLowerCase().includes(search)
        }
      })
    }
  } else if (search) {
    let temp = result
    result = temp.filter(review => {
      if (review.commentText) {
        return review.commentText.toLowerCase().includes(search)
      }
    })
  }

  if (starCountFilter) {
    result = result.filter(r => r.stars === starCountFilter)
  }
  return result
}

const Input = styled.input`
  border: solid black 1px;
  width: 300px;
`

const Header = styled.div``
const LinkWrapper = styled(Flex)`
  justify-content: flex-end;
`
const ButtonWrapper = styled(Flex)`
  text-align: center;
  align-items: center;
  justify-content: center;
`

const ReviewBlock = forwardRef((props, ref) => {
  const [selectedOption, setSelectedOption] = useState('')
  const [searchText, setSearchText] = useState('')
  const [starCountFilter, setStarCountFilter] = useState('')
  const [questionFormHidden, setQuestionFormHidden] = useState(true)
  const [reviewFormHidden, setReviewFormHidden] = useState(true)

  const { currentData, ...paginationComments } = usePagination(
    renderReviews(props.comments, selectedOption, searchText, starCountFilter),
    props.showNumOfComments,
    props.extraPage
  )

  const { currentData: questionData, ...paginationQuestions } = usePagination(
    props.questions,
    5
  )

  const questionTab =
    props.questions.length > 0
      ? {
          tabTitle: 'Question',
          component: (
            <>
              {questionData().map((question, index) => (
                <CommentBlock key={index} {...question} />
              ))}
              <Pagination {...paginationQuestions} />
            </>
          ),
        }
      : {}

  const reviewArr = currentData()
  const reviewImages = props.reviewImages
  return (
    <>
      <Flex
        ref={ref}
        sx={{
          justifyContent: [
            'center',
            null,
            null,
            null,
            !reviewImages ? 'center' : 'flex-start',
          ],
          textAlign: !reviewImages ? 'center' : 'left',
          border: 'commentBorder',
          alignItems: 'center',
          flexWrap: 'wrap',
          p: 25,
          scrollMarginTop: '17rem',
        }}
      >
        <Header
          sx={{
            textAlign: [
              'left',
              null,
              null,
              null,
              !reviewImages ? 'center' : 'left',
            ],
            justifyContent: [
              'flex-start',
              null,
              null,
              null,
              !reviewImages ? 'center' : 'flex-start',
            ],
            width: ['100%', null, null, null, 350],
          }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: [
                'none',
                null,
                null,
                null,
                !reviewImages ? 'center' : 'flex-start',
              ],
            }}
          >
            <Heading as="h5" variant="bold" sx={{ mr: '10px' }}>
              {typeof props.avgReviewScore === 'number'
                ? props.avgReviewScore.toFixed(1)
                : props.avgReviewScore}
            </Heading>

            {typeof props.avgReviewScore === 'number' && (
              <CustomStarRating
                name="averageReviewRating"
                rating={props.avgReviewScore}
                starDimension="25px"
              />
            )}
          </Flex>

          <Body
            variant="body2"
            sx={{
              mt: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Based on {props.numOfReviews} reviews powered by{' '}
            <img
              src={TrustPilotLogo}
              sx={{
                display: 'inline-block',
                ml: '5px',
                height: '20px',
                width: 'auto',
                mb: '3.5px',
              }}
            />
          </Body>
          <Box sx={{ py: 15 }}>
            {starFilters.map(sf => {
              const commentCount = props.comments.filter(
                r => r.stars === sf.count
              ).length

              const psuedoCount =
                (props.psuedoReviewCounts &&
                  props.psuedoReviewCounts[sf.text]) ||
                0

              return (
                <Flex
                  sx={{ alignItems: 'center', my: '15px', cursor: 'pointer' }}
                  onClick={() =>
                    commentCount > 0 && setStarCountFilter(sf.count)
                  }
                >
                  <Box sx={{ mr: 20, minWidth: 60 }}>{sf.count} Stars</Box>
                  <div sx={{ width: '100%', position: 'relative' }}>
                    <Text
                      sx={{
                        position: 'absolute',
                        top: '6px',
                        left: 10,
                        color: 'white',
                        zIndex: 1,
                        fontSize: 1,
                      }}
                    >
                      {commentCount + psuedoCount}
                    </Text>
                    <Progress
                      sx={{
                        height: 25,
                        borderRadius: '0px',
                        backgroundColor: 'lighterGray',
                        color: 'black',
                        position: 'relative',
                        border:
                          starCountFilter === sf.count && '2px solid black',
                      }}
                      max={1}
                      value={(commentCount + psuedoCount) / props.numOfReviews}
                    />
                  </div>
                </Flex>
              )
            })}
          </Box>
        </Header>
        {reviewImages && (
          <Flex>
            <Grid
              gap={2}
              columns={[2, 3, 3, 2, 4]}
              sx={{ margin: ['auto', null, null, 'auto 60px'], width: 'auto' }}
            >
              <LightboxImageCarousel
                images={reviewImages}
                alt="Review Images"
              />
            </Grid>
          </Flex>
        )}
      </Flex>

      <Flex
        sx={{
          justifyContent: 'space-between',
          py: 10,
        }}
      >
        <Box>
          {props.search && (
            <Input
              sx={{}}
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search Reviews"
              sx={{ m: '5px', pl: '10px', border: 'default', borderRadius: 4 }}
            />
          )}
          {props.options && (
            <Dropdown
              sx={{
                m: '5px',
                width: '150px',
              }}
              options={props.options}
              onChange={e => setSelectedOption(e)}
              placeholder="All"
            />
          )}
        </Box>
        <Box>
          {props.sortOptions && (
            <Dropdown
              sx={{
                m: '5px',
                width: '150px',
              }}
              options={props.sortOptions}
              onChange={e => setSelectedOption(e)}
              placeholder="All"
            />
          )}
        </Box>
      </Flex>

      {props.showForms && (
        <HideOnMobile>
          <Box
            sx={{
              mt: '44px',
            }}
          >
            {/* <LinkWrapper>
              <Link
                sx={{ mr: '32px' }}
                onClick={() => setReviewFormHidden(!reviewFormHidden)}
              >
                Write a Review
              </Link>

              <Link onClick={() => setQuestionFormHidden(!questionFormHidden)}>
                Ask a Question
              </Link>
            </LinkWrapper> */}
          </Box>
        </HideOnMobile>
      )}

      {props.showForms && (
        <HideOnDestop>
          <ButtonWrapper
            sx={{
              mb: '25px',
              mt: '20px',
            }}
          >
            <Button
              onClick={() => setReviewFormHidden(!reviewFormHidden)}
              variant="white"
              sx={{
                mr: [15, 15, 40],
                border: '2px solid #000000',
                px: 15,
              }}
            >
              <Body variant="body2" sx={{ color: 'text' }}>
                Write a Review
              </Body>
            </Button>
            <Button
              variant="white"
              sx={{ border: '2px solid #000000', px: 15 }}
              onClick={() => setQuestionFormHidden(!questionFormHidden)}
            >
              <Body variant="body2" sx={{ color: 'text' }}>
                Ask a Question
              </Body>
            </Button>
          </ButtonWrapper>
        </HideOnDestop>
      )}

      {!reviewFormHidden && (
        <ReviewForm
          hidden={() => setReviewFormHidden(!reviewFormHidden)}
          productName={props.productName}
        />
      )}
      {!questionFormHidden && (
        <QuestionForm
          hidden={() => setQuestionFormHidden(!questionFormHidden)}
          productName={props.productName}
        />
      )}

      {!props.noTabs ? (
        <Box>
          <Tab
            renderItems={[
              {
                tabTitle: 'Review',
                component: (
                  <>
                    {reviewArr.length > 0 ? (
                      reviewArr.map((comment, index) => (
                        <CommentBlock key={index} {...comment} />
                      ))
                    ) : (
                      <Flex
                        sx={{
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 300,
                          flexDirection: 'column',
                        }}
                      >
                        <Body>Interested in more reviews?</Body>
                        <Button variant="black" to="/reviews" sx={{ mt: 20 }}>
                          Read them here
                        </Button>
                      </Flex>
                    )}
                    <Pagination {...paginationComments} />
                  </>
                ),
              },
              questionTab,
            ]}
          />
        </Box>
      ) : (
        <>
          {reviewArr.map((comment, index) => (
            <CommentBlock
              style={{ marginTop: index === 1 && '0px' }}
              key={index}
              {...comment}
            />
          ))}
          <Pagination {...paginationComments} />
        </>
      )}
    </>
  )
})

export default ReviewBlock
